// Client-side syntax check for Compute's expression DSL. Mirrors what
// apply_expression (server/app/engine/helpers.py) and pipeline_validate.py
// accept: a literal, {column}, BLANK, or FN(arg, …) with FN in the list below.
// Returns an error string, or null when the expression looks valid.
const FN_ARGS = {
  UPPER: [1, 1], LOWER: [1, 1], TITLE: [1, 1], TRIM: [1, 1], LEN: [1, 1],
  CONCAT: [1, Infinity], REPLACE: [2, 3], LEFT: [2, 2], RIGHT: [2, 2],
};

export function checkExpression(expr, columnNames) {
  const e = (expr || '').trim();
  if (!e) return null;

  // Quotes, braces and parentheses must all be balanced.
  let quote = null, depth = 0, inBrace = false;
  for (const ch of e) {
    if (quote) { if (ch === quote) quote = null; continue; }
    if (inBrace) { if (ch === '}') inBrace = false; continue; }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '{') inBrace = true;
    else if (ch === '(') depth++;
    else if (ch === ')' && --depth < 0) return 'Unexpected ")" — check the parentheses.';
  }
  if (quote) return `Missing closing ${quote} — check the quotes.`;
  if (inBrace) return 'Missing closing "}" in a {column} reference.';
  if (depth > 0) return 'Missing closing ")" — check the parentheses.';

  const m = /^([A-Za-z_]+)\s*\(([\s\S]*)\)$/.exec(e);
  if (/^[A-Za-z_]+\s*\(/.test(e) && !m) return 'Text found after the closing ")" — check the expression.';
  if (m) {
    const fn = m[1].toUpperCase();
    const spec = FN_ARGS[fn];
    if (!spec) return `Unknown function "${m[1]}" — use UPPER, LOWER, TITLE, TRIM, CONCAT, REPLACE, LEFT, RIGHT or LEN.`;
    let d = 0, q = null, cur = '';
    const args = [];
    for (const ch of m[2]) {
      if (q) { cur += ch; if (ch === q) q = null; continue; }
      if (ch === '"' || ch === "'") { q = ch; cur += ch; }
      else if (ch === '(') { d++; cur += ch; }
      else if (ch === ')') { d--; cur += ch; }
      else if (ch === ',' && d === 0) { args.push(cur); cur = ""; }
      else cur += ch;
    }
    if (cur.trim() || args.length) args.push(cur);
    if (args.some((a) => !a.trim())) return `${fn}() has an empty argument — check the commas.`;
    if (args.length < spec[0] || args.length > spec[1]) {
      const want = spec[1] === Infinity ? `at least ${spec[0]}` : spec[0] === spec[1] ? `${spec[0]}` : `${spec[0]}–${spec[1]}`;
      return `${fn}() takes ${want} argument${spec[1] === 1 ? '' : 's'}, found ${args.length}.`;
    }
  } else if (/[(),]/.test(e.replace(/"[^"]*"|'[^']*'|\{[^}]*\}/g, ''))) {
    return 'Not a valid expression — check the formula.';
  }

  if (columnNames && columnNames.length) {
    for (const [, name] of e.matchAll(/\{([^}]*)\}/g)) {
      if (!columnNames.includes(name)) return `Column "${name}" doesn't exist in this Source — check the name.`;
    }
  }
  return null;
}
